import { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "@inertiajs/react"

import { HeroPreload } from "../Common/HeroPreload"
import { Image } from "../Common/Image"
import { getPlaceholderImage } from "../GetItem/data/photo-data"
import dayjs from "../../lib/dayjs"
import { HERO_WIDTHS, HOME_HERO_SIZES } from "../../utils/imageUrl"

const AUCTION_ENDED = "Auction ended"

const calculateTimeLeft = (endTime) => {
    const difference = +new Date(endTime) - +new Date()
    if (difference <= 0) {
        return AUCTION_ENDED
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24))
    const hours = Math.floor((difference / (1000 * 60 * 60)) % 24)
    const minutes = Math.floor((difference / 1000 / 60) % 60)

    if (days > 0) {
        return `${days}d ${hours}h left`
    }

    return `${hours}h ${minutes}m left`
}

// `date_sold` is a date serialised as UTC midnight. Formatting it in UTC keeps
// the sale day, and keeps the server and client renders in agreement; local
// time would put it a day early anywhere west of Greenwich.
const formatSoldDate = (dateSold) => dayjs.utc(dateSold).format("MM/DD/YYYY")

/**
 * The hero's listings, one per view on a native scroll-snap track, so a swipe
 * is the browser's own and a tap without movement follows the slide's link.
 *
 * Every slide is in the server-rendered HTML: most of these listings are linked
 * nowhere else on the homepage. Only the first photo loads eagerly; it is the
 * page's LCP element and is preloaded with the same widths and sizes, so the
 * preload and the <img> resolve to one request.
 *
 * The arrows are siblings of the track, never inside a link. Slides out of
 * view are aria-hidden and untabbable, kept in step with the scroll position
 * so a swipe updates them too. The countdown is computed once after mount,
 * as before; it is blank in the server HTML and does not tick.
 *
 * With nothing live, the server sends recently sold listings instead, and
 * only then: each shows its sale date in place of the price, live dot and
 * countdown.
 */
export const HeroListingCarousel = ({ listings, soldListings }) => {
    const isSold = soldListings.length > 0
    const slides = useMemo(
        () =>
            (isSold ? soldListings : listings).filter(
                (listing) => listing?.gunbroker_item_id
            ),
        [isSold, listings, soldListings]
    )
    const [current, setCurrent] = useState(0)
    const [timeLeft, setTimeLeft] = useState({})
    const track = useRef(null)

    useEffect(() => {
        if (isSold) return

        setTimeLeft(
            Object.fromEntries(
                slides.map((listing, index) => [
                    index,
                    calculateTimeLeft(listing.ending_time),
                ])
            )
        )
    }, [isSold, slides])

    const syncCurrent = () => {
        const element = track.current
        if (!element?.clientWidth) return

        setCurrent(Math.round(element.scrollLeft / element.clientWidth))
    }

    const goTo = (target) => {
        const next = (target + slides.length) % slides.length
        const element = track.current

        setCurrent(next)

        if (!element) return

        const left = next * element.clientWidth

        // A smooth wrap would rewind visibly through every listing; jump instead.
        if (next !== target) {
            element.style.scrollBehavior = "auto"
            element.scrollLeft = left
            element.style.scrollBehavior = ""

            return
        }

        element.scrollTo({ left })
    }

    return (
        <div
            className="falh-carousel"
            role="region"
            aria-roledescription="carousel"
            aria-label={
                isSold ? "Recently sold listings" : "Featured auction listings"
            }
        >
            <HeroPreload
                src={slides[0]?.gun?.photo_array?.[0]}
                sizes={HOME_HERO_SIZES}
                widths={HERO_WIDTHS}
            />

            <div ref={track} className="falh-track" onScroll={syncCurrent}>
                {slides.map((listing, index) => {
                    const isCurrent = index === current

                    return (
                        <Link
                            key={listing.gunbroker_item_id}
                            href={route("item", {
                                auction: listing.gunbroker_item_id,
                            })}
                            className="falh-slide"
                            aria-hidden={isCurrent ? undefined : "true"}
                            tabIndex={isCurrent ? undefined : -1}
                        >
                            <Image
                                src={listing.gun.photo_array?.[0] ?? null}
                                alt=""
                                width={950}
                                height={633}
                                widths={HERO_WIDTHS}
                                sizes={HOME_HERO_SIZES}
                                eager={index === 0}
                                priority={index === 0 ? "high" : undefined}
                                fallbackSrc={
                                    index === 0
                                        ? undefined
                                        : getPlaceholderImage(listing.id)
                                }
                            />
                            <span className="falh-slide-veil" aria-hidden="true" />
                            <span className="falh-listing">
                                <span className="sr-only">
                                    {`Listing ${index + 1} of ${slides.length}`}
                                </span>
                                <span className="falh-title">
                                    {listing.gun.title_for_gunbroker}
                                </span>
                                {isSold ? (
                                    <span className="falh-meta">
                                        <span className="falh-sold">
                                            {`Sold On ${formatSoldDate(
                                                listing.date_sold
                                            )}`}
                                        </span>
                                    </span>
                                ) : (
                                    <span className="falh-meta">
                                        <span className="falh-price">
                                            {`$${
                                                listing.current_bid
                                                    ? listing.current_bid.toLocaleString(
                                                          "en-US"
                                                      )
                                                    : "0"
                                            }`}
                                        </span>
                                        <span
                                            className="falh-live"
                                            data-ended={
                                                timeLeft[index] ===
                                                AUCTION_ENDED
                                                    ? "true"
                                                    : undefined
                                            }
                                            aria-hidden="true"
                                        />
                                        <span className="falh-count">
                                            {timeLeft[index]}
                                        </span>
                                    </span>
                                )}
                            </span>
                        </Link>
                    )
                })}
            </div>

            {slides.length > 1 && (
                <>
                    <button
                        type="button"
                        className="falh-arrow falh-arrow-prev"
                        aria-label="Previous listing"
                        onClick={() => goTo(current - 1)}
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M15 6l-6 6 6 6" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        className="falh-arrow falh-arrow-next"
                        aria-label="Next listing"
                        onClick={() => goTo(current + 1)}
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M9 6l6 6-6 6" />
                        </svg>
                    </button>
                </>
            )}
        </div>
    )
}
